import { checkScheduleAssignments } from './jobIntegrity';
import { validateVehicleChain } from './validateVehicleChain';
import {
  formatIssuesEnglish,
  type ScheduleValidationIssue,
} from './validationIssues';
import { clearPinIfPresent, retargetPin } from './planPins';
import type {
  DailySchedulePlan,
  ScheduleJobInput,
  ScheduleLegInput,
  ScheduleVehicleInput,
  TravelTimeOptions,
  VehicleChainPlan,
} from './types';

export type MutatePlanResult =
  | { ok: true; plan: DailySchedulePlan }
  | { ok: false; errors: string[]; issues: ScheduleValidationIssue[]; plan: DailySchedulePlan };

function fail(
  plan: DailySchedulePlan,
  issues: ScheduleValidationIssue[]
): MutatePlanResult {
  return { ok: false, issues, errors: formatIssuesEnglish(issues), plan };
}

function travelOf(plan: DailySchedulePlan): TravelTimeOptions {
  return plan.travelOptions ?? {};
}

function jobsOf(plan: DailySchedulePlan): ScheduleJobInput[] {
  return plan.jobs ?? [];
}

function fleetOf(plan: DailySchedulePlan): ScheduleVehicleInput[] {
  return plan.fleet ?? [];
}

function insertLegSorted(chain: ScheduleLegInput[], leg: ScheduleLegInput): ScheduleLegInput[] {
  const next = [...chain, leg];
  next.sort((a, b) => a.earliestStartMinutes - b.earliestStartMinutes);
  return next;
}

function rebuildVehiclePlans(
  chains: Map<string, ScheduleLegInput[]>,
  fleet: ScheduleVehicleInput[],
  travelOptions: TravelTimeOptions,
  previous?: VehicleChainPlan[]
): VehicleChainPlan[] {
  const fleetMap = new Map(fleet.map((v) => [v.id, v]));
  const prevDrivers = new Map(
    (previous ?? []).map((v) => [v.vehicleId, v.driverId] as const)
  );
  const plans: VehicleChainPlan[] = [];
  for (const [vehicleId, legs] of chains) {
    if (legs.length === 0) continue;
    const meta = fleetMap.get(vehicleId);
    plans.push({
      vehicleId,
      legs,
      fleetNumber: meta?.fleetNumber,
      driverId: prevDrivers.has(vehicleId) ? prevDrivers.get(vehicleId) : undefined,
      validation: validateVehicleChain(legs, travelOptions),
    });
  }
  plans.sort((a, b) => {
    const aStart = a.legs[0]?.earliestStartMinutes ?? 0;
    const bStart = b.legs[0]?.earliestStartMinutes ?? 0;
    return aStart - bStart;
  });
  return plans;
}

function finalizePlan(
  base: DailySchedulePlan,
  chains: Map<string, ScheduleLegInput[]>,
  travelOptions: TravelTimeOptions
): DailySchedulePlan {
  const jobs = jobsOf(base);
  const fleet = fleetOf(base);
  const vehicles = rebuildVehiclePlans(chains, fleet, travelOptions, base.vehicles);
  const allJobIds = jobs.filter((j) => j.legs.length > 0).map((j) => j.id);
  const integrity = checkScheduleAssignments(
    allJobIds,
    vehicles.map((v) => ({ vehicleId: v.vehicleId, legs: v.legs }))
  );
  const assigned = new Set(vehicles.flatMap((v) => v.legs.map((l) => l.jobId)));
  const unassignedJobIds = [
    ...new Set([...integrity.unassignedJobIds, ...allJobIds.filter((id) => !assigned.has(id))]),
  ];
  const jobsById = new Map(jobs.map((j) => [j.id, j]));
  return {
    ...base,
    jobs,
    fleet,
    travelOptions,
    fixedLegsByVehicle: base.fixedLegsByVehicle,
    jobTitles: base.jobTitles,
    vehicles,
    unassignedJobIds,
    unassignedJobs: unassignedJobIds.map((id) => ({
      id,
      title: jobsById.get(id)?.title ?? id,
    })),
    duplicateJobs: integrity.duplicateJobs,
    metrics: {
      vehicleCount: vehicles.length,
      assignedJobCount: allJobIds.length - unassignedJobIds.length,
      totalDeadheadKm: vehicles.reduce((s, v) => s + v.validation.totalDeadheadKm, 0),
      allFeasible: vehicles.every((v) => v.validation.ok) && unassignedJobIds.length === 0,
    },
  };
}

function chainsFromPlan(plan: DailySchedulePlan): Map<string, ScheduleLegInput[]> {
  const chains = new Map<string, ScheduleLegInput[]>();
  for (const v of plan.vehicles) {
    chains.set(v.vehicleId, [...v.legs]);
  }
  return chains;
}

function removeJobLegs(chains: Map<string, ScheduleLegInput[]>, jobId: string): void {
  for (const [vehicleId, legs] of chains) {
    const next = legs.filter((l) => l.isFixed || l.jobId !== jobId);
    if (next.length === 0) chains.delete(vehicleId);
    else chains.set(vehicleId, next);
  }
}

/**
 * Place all legs of a job onto a vehicle (manual override).
 * Rejects when capacity is insufficient or the resulting chain is infeasible.
 */
export function assignJobToVehicle(
  plan: DailySchedulePlan,
  jobId: string,
  vehicleId: string
): MutatePlanResult {
  const jobs = jobsOf(plan);
  const fleet = fleetOf(plan);
  const travelOptions = travelOf(plan);
  const job = jobs.find((j) => j.id === jobId);
  const vehicle = fleet.find((v) => v.id === vehicleId);

  if (!job || job.legs.length === 0) {
    return fail(plan, [{ code: 'JOB_NOT_IN_PLAN', params: { jobId } }]);
  }
  if (job.legs.some((l) => l.isFixed)) {
    return fail(plan, [{ code: 'FIXED_CANNOT_REASSIGN', params: { jobId } }]);
  }
  if (!vehicle) {
    return fail(plan, [{ code: 'VEHICLE_NOT_FOUND', params: { vehicleId } }]);
  }
  if (
    job.fleetOperatorId &&
    vehicle.fleetOperatorId !== job.fleetOperatorId
  ) {
    return fail(plan, [
      {
        code: 'FLEET_MISMATCH',
        params: {
          vehicle: vehicle.fleetNumber ?? vehicleId,
        },
      },
    ]);
  }
  if (vehicle.capacity < job.passengerCount) {
    return fail(plan, [
      {
        code: 'CAPACITY_SHORT',
        params: {
          vehicle: vehicle.fleetNumber ?? vehicleId,
          capacity: vehicle.capacity,
          passengers: job.passengerCount,
        },
      },
    ]);
  }

  const chains = chainsFromPlan(plan);
  removeJobLegs(chains, jobId);

  let chain = chains.get(vehicleId) ?? [];
  for (const leg of [...job.legs].sort((a, b) => a.earliestStartMinutes - b.earliestStartMinutes)) {
    chain = insertLegSorted(chain, leg);
  }
  chains.set(vehicleId, chain);

  const validation = validateVehicleChain(chain, travelOptions);
  if (!validation.ok) {
    return fail(plan, validation.issues);
  }

  const next = retargetPin(finalizePlan(plan, chains, travelOptions), jobId, vehicleId);
  return { ok: true, plan: next };
}

/** Move a job from its current vehicle onto another (or assign if unassigned). */
export function reassignJobToVehicle(
  plan: DailySchedulePlan,
  jobId: string,
  vehicleId: string
): MutatePlanResult {
  return assignJobToVehicle(plan, jobId, vehicleId);
}

/** Remove a job from all vehicle chains back to unassigned. */
export function unassignJobFromPlan(plan: DailySchedulePlan, jobId: string): DailySchedulePlan {
  const chains = chainsFromPlan(plan);
  for (const legs of chains.values()) {
    if (legs.some((l) => l.jobId === jobId && l.isFixed)) {
      return plan;
    }
  }
  removeJobLegs(chains, jobId);
  return clearPinIfPresent(finalizePlan(plan, chains, travelOf(plan)), jobId);
}
